/**
 * Animation Preferences Utility
 * Manages user preferences for UI animations with sensible defaults
 */

const ANIMATION_STORAGE_KEY = 'queryburst_animation_preferences';

/**
 * Default animation preferences
 */
const DEFAULT_PREFERENCES = {
  sparkleTrails: true,
  transitions: true,
  autoAnimations: true,
  reducedMotion: false
};

/**
 * Get animation preferences from localStorage
 * @returns {Object} Animation preferences object
 */
export const getAnimationPreferences = () => {
  try {
    // Check for system preference for reduced motion
    const prefersReducedMotion = window.matchMedia && 
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    const stored = localStorage.getItem(ANIMATION_STORAGE_KEY);
    const preferences = stored ? JSON.parse(stored) : DEFAULT_PREFERENCES;
    
    // If user prefers reduced motion but hasn't explicitly set preferences,
    // respect their system setting
    if (prefersReducedMotion && !stored) {
      preferences.sparkleTrails = false;
      preferences.transitions = false;
      preferences.autoAnimations = false;
      preferences.reducedMotion = true;
    }
    
    return { ...DEFAULT_PREFERENCES, ...preferences };
  } catch (error) {
    console.warn('Failed to load animation preferences:', error);
    return DEFAULT_PREFERENCES;
  }
};

/**
 * Save animation preferences to localStorage
 * @param {Object} preferences - Animation preferences to save
 */
export const setAnimationPreferences = (preferences) => {
  try {
    const current = getAnimationPreferences();
    const updated = { ...current, ...preferences };
    localStorage.setItem(ANIMATION_STORAGE_KEY, JSON.stringify(updated));
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('animationPreferencesChanged', {
      detail: updated
    }));
    
    return updated;
  } catch (error) {
    console.warn('Failed to save animation preferences:', error);
    return getAnimationPreferences();
  }
};

/**
 * Check if specific animation type is enabled
 * @param {string} animationType - Type of animation to check
 * @returns {boolean} Whether the animation is enabled
 */
export const isAnimationEnabled = (animationType = 'sparkleTrails') => {
  const preferences = getAnimationPreferences();
  return preferences[animationType] === true;
};

/**
 * Toggle a specific animation preference
 * @param {string} animationType - Type of animation to toggle
 * @returns {boolean} New state of the animation
 */
export const toggleAnimation = (animationType) => {
  const current = getAnimationPreferences();
  const newState = !current[animationType];
  setAnimationPreferences({ [animationType]: newState });
  return newState;
};

/**
 * Reset all animation preferences to defaults
 */
export const resetAnimationPreferences = () => {
  try {
    localStorage.removeItem(ANIMATION_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('animationPreferencesChanged', {
      detail: DEFAULT_PREFERENCES
    }));
    return DEFAULT_PREFERENCES;
  } catch (error) {
    console.warn('Failed to reset animation preferences:', error);
    return DEFAULT_PREFERENCES;
  }
};

/**
 * Hook for listening to animation preference changes
 * @param {Function} callback - Callback to execute when preferences change
 */
export const onAnimationPreferencesChanged = (callback) => {
  const handleChange = (event) => callback(event.detail);
  window.addEventListener('animationPreferencesChanged', handleChange);
  
  // Return cleanup function
  return () => window.removeEventListener('animationPreferencesChanged', handleChange);
};
